import React, { useState, useEffect } from 'react';
import { useLocation, useParams, Link, useNavigate } from 'react-router-dom';
import PhoneIcon from "../../../images/phone-icon.svg";
import AddressIcon from "../../../images/address-icon.svg";
import MailIcon from "../../../images/email-icon.png";
import IconHome from "../../../images/icon-home.png";
import { Tab, Tabs, Spinner, Button } from 'react-bootstrap';
import { API_ENDPOINTS } from '../../utils.ts';
import SubscriptionTab from './SubscriptionTab.tsx';
import LegacyContactTab from './LegacyContactTab.tsx';
import PaymentInfoTab from './PaymentInfoTab.tsx';
import axiosInstance from '../../../AxiosInstance.tsx';
import { toast } from 'react-toastify';
import { useApiLoader } from "../../../hooks/useApiLoader.tsx";
import Gift from './Gifts.tsx';
import { formatPhoneNumber } from '../../../utils/phoneFormatter';
import { FaArrowLeftLong } from "react-icons/fa6";
import "../GiftManagement/GiftManagement.scss";

const BillingDetails: React.FC = () => {
  const { withLoader } = useApiLoader();
  const location = useLocation();
  const navigate = useNavigate();
  const { id: userIdFromParams } = useParams<{ id: string }>();
  const [activeKey, setActiveKey] = useState<string>('gifts');
  const [userData, setUserData] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Get userId from params or location state
  const userId = userIdFromParams || location.state?.userId || location.state?.customer?.id;

  useEffect(() => {
    if (userId) {
      fetchUserData(userId);
    } else {
      setError('User ID not provided');
      setLoading(false);
    }
  }, [userId]);

  const fetchUserData = async (id: string) => {
    try {
      setLoading(true);
      setError(null);

      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        toast.error('Authentication required. Please login again.');
        return;
      }

      const response = await withLoader(
        () => axiosInstance.get(
          `${API_ENDPOINTS.GET_SINGLE_USER}/${id}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
          }
        ),
        'Loading user billing details...'
      );

      if (response.data.status === "Success") {
        setUserData(response.data.data);
      } else {
        setError(response.data.message || "Failed to fetch user data");
        toast.error(response.data.message || "Failed to fetch user data");
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch user data";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Get initials from name (similar to Header component)
  const getInitials = (name: string) => {
    if (!name) return 'U'; // Default to 'U' for User if no name
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  // Capitalize text helper
  const capitalizeText = (text: string) => {
    if (!text) return '';
    return text
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getStatusClass = (status: string | undefined | null): string => {
    return typeof status === "string" && status.toLowerCase() === "active"
      ? "tag active"
      : "tag inactive";
  };

  // Handle loading state
  if (loading) {
    return (
      <section className='billing-profile-sec'>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </section>
    );
  }

  // Handle error state
  if (error || !userData) {
    return (
      <section className='billing-profile-sec'>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <h4>Error Loading User Data</h4>
            <p>{error || 'User data not found'}</p>
            <button
              className="btn btn-primary"
              onClick={() => userId && fetchUserData(userId)}
            >
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className='billing-profile-sec'>
      {/* Breadcrumb Navigation */}
      <div className="row align-items-center mb-3">
        <div className="col-md-8">
          <nav className="breadcrumb-nav">
            <Link to="/users" className="text-decoration-none" style={{color:'#3957A0'}}>
              <span><img src={IconHome} alt="home" /> <span className='my-auto'>Customers</span></span>
            </Link>
            <span className="mx-2 text-muted">›</span>
            <span className="text-dark">{userData?.customerName || 'User Details'}</span>
          </nav>
        </div>
        <div className="col-md-4 text-end">
          <Button color="secondary" className='bg-transparent border-0 fw-bold' style={{color:'#3957A0'}} onClick={() => navigate('/users')}>
            <FaArrowLeftLong color='#3957A0'/> Back
          </Button>
        </div>
      </div>

      {/* Profile Header */}
      <div className='profile-area'>
        <div className='row justify-content-between' style={{ alignItems: "start" }}>
          <aside className='profile-block col-xl-3 col-lg-3'>
            <figure>
              {userData?.profileUrl ? (
                <img
                  src={userData.profileUrl}
                  alt={userData?.customerName || "Profile"}
                  onError={(e) => {
                    // Fallback to initials if image fails to load
                    e.currentTarget.style.display = 'none';
                    const parent = e.currentTarget.parentElement;
                    if (parent) {
                      const fallbackDiv = document.createElement('div');
                      fallbackDiv.className = 'profile-circle-grid rounded-circle  text-white d-flex justify-content-center align-items-center';
                      fallbackDiv.style.width = '100px';
                      fallbackDiv.style.height = '100px';
                      fallbackDiv.textContent = getInitials(userData?.customerName);
                      parent.appendChild(fallbackDiv);
                    }
                  }}
                />
              ) : (
                <div className="profile-circle-grid rounded-circle  text-white d-flex justify-content-center align-items-center" style={{ width: '100px', height: '100px' }}>
                  {getInitials(userData?.customerName)}
                </div>
              )}
            </figure>
            <div className="profile-cont">
              <h3>{capitalizeText(userData?.customerName) || "No name provided"}</h3>
              <p className='my-1'>Referral: <b className='text-black'>GFH2958</b></p>
              <span className={getStatusClass(userData?.accountStatus)}>
                {capitalizeText(userData?.accountStatus) || "Unknown"}
              </span>
            </div>
          </aside>
          <aside className='profile-det col-xl-3 col-lg-3 mt-3'>
            <div className='profile-det-in'>
              <div className='icon'>
                <img src={MailIcon} alt='Phone' />
              </div>
              <div className='profile-det-cont'>
                <h3>Email</h3>
                <p>{userData?.email || "Not provided"}</p>
              </div>
            </div>
          </aside>
          {/* <aside className='profile-det col-xl-3 col-lg-3 mt-3'>
            <div className='profile-det-in'>
              <div className='icon'>
                <img src={PhoneIcon} alt='Phone' />
              </div>
              <div className='profile-det-cont'>
                <h3>Phone</h3>
                <p>{formatPhoneNumber(userData?.phone) || "Not provided"}</p>
              </div>
            </div>
          </aside> */}
                    <aside className='profile-det col-xl-3 col-lg-3 mt-3'>
            <div className='profile-det-in'>
              <div className='icon'>
                <img src={AddressIcon} alt='Address' />
              </div>
              <div className='profile-det-cont'>
                <h3>Phone</h3>
                <p>{formatPhoneNumber(userData?.phone) || "Not provided"}</p>
              </div>
            </div>
          </aside>
          <aside className='profile-det col-xl-3 col-lg-3 mt-3'>
            <div className='profile-det-in'>
              <div className='icon'>
                <img src={AddressIcon} alt='Address' />
              </div>
              <div className='profile-det-cont'>
                <h3>Address</h3>
                <p>
                  {userData?.address1 || userData?.city || userData?.state || userData?.zip
                    ? [
                        userData?.address1,
                        userData?.city,
                        userData?.state,
                        userData?.zip
                      ].filter(Boolean).join(', ')
                    : 'Not provided'
                  }
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="main-tabs user-tabs">
        <div className='tabs'>
          <Tabs
            id="billing-tabs"
            activeKey={activeKey}
            onSelect={(k) => setActiveKey(k || 'gifts')}
            className="mb-3 custom-tabs"
          >
            <Tab eventKey="gifts" title="Gifts">
              <Gift userId={userId} isActive={activeKey === 'gifts'} />
            </Tab>

            <Tab eventKey="billing" title="Subscription & Billing Details">
              <SubscriptionTab userData={userData} />
            </Tab>

            <Tab eventKey="legacy" title="Legacy Contact Information">
              <LegacyContactTab userData={userData} />
            </Tab>

            <Tab eventKey="payments" title="Payment Information">
              <PaymentInfoTab
                userId={userId}
                isActive={activeKey === 'payments'}
              />
            </Tab>
          </Tabs>
        </div>
      </div>
    </section>
  );
};

export default BillingDetails;